import {
    addValueToUserPref,
    decodeValue,
    encodeValue,
    getValueFromUserPrefCookieValue,
} from './user-pref-cookie-service';

export const RECENT_LOC_PREF_COOKIE = 'gt_recentLocPref';

const UK_LOCATION_SEGMENT_PATTERN = /\/uk\/(?!page\d)([^/]+)/;

/**
 * True when the URL path encodes a SEO location (e.g. /pets/uk/london or .../uk/london/srpsearch+plumber).
 * Paths where /uk/ is immediately followed by srpsearch+... are search URLs, not location URLs.
 */
export const pathHasLocationInUrl = (path: string) => {
    if (/\/uk\/?$/.test(path)) {
        return true;
    }

    const match = path.match(UK_LOCATION_SEGMENT_PATTERN);
    if (!match) {
        return false;
    }

    return !match[1].toLowerCase().startsWith('srpsearch');
};

export const isUnitedKingdomLocation = (location: string) =>
    /^united\s+kingdom$/i.test(location.trim());

/**
 * SEO location slug from a BRP path segment after `/uk/` (e.g. `london` from `.../uk/london`).
 * Returns undefined for UK-wide paths (`/uk` only), pagination (`/uk/page2`), or search paths (`/uk/srpsearch+...`).
 */
export const getLocationSegmentFromBrpPath = (path: string): string | undefined => {
    if (!path || /\/uk\/?$/.test(path)) {
        return undefined;
    }

    const match = path.match(UK_LOCATION_SEGMENT_PATTERN);
    if (!match) {
        return undefined;
    }

    const segment = match[1];
    const segmentLower = segment.toLowerCase();

    if (segmentLower === 'page' || segmentLower.startsWith('page') || segmentLower.startsWith('srpsearch')) {
        return undefined;
    }

    return segment;
};

export const getCookieValueFromHeader = (
    cookieHeader: string | undefined,
    name: string
): string | undefined => {
    if (!cookieHeader) {
        return undefined;
    }

    const prefix = `${name}=`;
    const cookie = cookieHeader.split('; ').find((entry) => entry.startsWith(prefix));

    return cookie ? cookie.substring(prefix.length) : undefined;
};

export const encodeRecentLocPref = (locationName: string): string | undefined =>
    encodeValue(locationName);

export const decodeRecentLocPref = (encoded: string | undefined): string | undefined =>
    decodeValue(encoded);

export const getRecentLocPrefFromCookieHeader = (
    cookieHeader: string | undefined
): string | undefined => decodeRecentLocPref(getCookieValueFromHeader(cookieHeader, RECENT_LOC_PREF_COOKIE));

export const getUserPrefLocationFromCookieHeader = (
    cookieHeader: string | undefined
): string | undefined =>
    getValueFromUserPrefCookieValue(getCookieValueFromHeader(cookieHeader, 'gt_userPref'), 'location');

/**
 * True when the Referer is from the same host as the current request (in-site navigation,
 * including multi-hop BRP jumps and target="_blank" opens from Gumtree pages).
 */
export const refererIsSameSite = (
    referer: string | undefined,
    requestHost?: string
): boolean => {
    if (!referer || !requestHost) {
        return false;
    }

    try {
        return new URL(referer).host.toLowerCase() === requestHost.toLowerCase();
    } catch {
        return false;
    }
};

/**
 * Keep gt_userPref.location when the user arrived from an in-site Referer (any path).
 * Direct URL entry, bookmarks, and external links have no same-site Referer → use Recent[0] instead.
 */
export const shouldHonorUrlLocationInUserPref = (params: {
    cookieHeader: string | undefined;
    referer?: string | string[];
    requestHost?: string;
}): boolean => {
    const referer = Array.isArray(params.referer) ? params.referer[0] : params.referer;

    if (!refererIsSameSite(referer, params.requestHost)) {
        return false;
    }

    const userPrefLocation = getUserPrefLocationFromCookieHeader(params.cookieHeader);
    return !!userPrefLocation && !isUnitedKingdomLocation(userPrefLocation);
};

export const shouldApplyRecentLocationPrefForBrp = (params: {
    isSrp4B: boolean;
    path: string;
    cookieHeader: string | undefined;
    referer?: string | string[];
    requestHost?: string;
}): boolean => {
    if (!params.isSrp4B || /^\/search\b/.test(params.path) || pathHasLocationInUrl(params.path)) {
        return false;
    }

    const recentLocation = getRecentLocPrefFromCookieHeader(params.cookieHeader);
    if (!recentLocation || isUnitedKingdomLocation(recentLocation)) {
        return false;
    }

    return !shouldHonorUrlLocationInUserPref(params);
};

export const applyRecentLocationPrefToUserPrefCookie = (
    cookieHeader: string,
    recentLocation: string
): string | undefined => addValueToUserPref(cookieHeader, 'location', recentLocation);
